import { CheckCircleOutlined, CloseCircleOutlined, NodeIndexOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Button, Tag } from "antd";
import { useExportStructureStepLogsMutation, useGetStructureStepLogsQuery } from "api/TenantSlug/StepLog/hooks";
import { StructureStepLogDetailDrawer } from "components/Drawers/StructureStepLogDetailDrawer/StructureStepLogDetailDrawer";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { ConnectionCreatorScenarioCategory, PermissionEnum, SearchFilterEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";
import { STRUCTURE_SCENARIO_CATEGORIES } from "utils/scenarioFormStructure";

import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetStepLogStructuresResponse = App.Http.Responses.StepLog.GetStepLogStructuresResponse;
import StepLogStructureData = App.Data.StepLogStructureData;

import { TenantLink } from "../../UI/TenantLink/TenantLink.tsx";

interface SearchParams {
    "structureStepLogDetail-id"?: string;
}

export const StructureStepLogTable = () => {
    const tableId = TableId.StructureStepLogTable;
    const { t } = usePaldockTranslation("tables.structure-step-log");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tGA } = usePaldockTranslation("drawers.general-actions");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const getStepTypeLabel = useStepTypeLabel();
    const tableAtomInstance = tableAtom<StepLogStructureData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                "stepRun.scenarioRun.scenario.category": STRUCTURE_SCENARIO_CATEGORIES.join(","),
                ...(tableState.filter?.filter ?? {}),
                ...("stepRun.started_at" in (tableState.filter?.filter ?? {})
                    ? {}
                    : {
                          "stepRun.started_at": {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-stepRun.started_at",
            include:
                "formField,conversionState,stepRun,stepRun.scenarioRun,stepRun.scenarioRun.scenario,formField.formStructure",
        },
    };

    const getStructureStepLogsQuery = useGetStructureStepLogsQuery(request, { enabled: false });
    const getStructureStepLogsExportMutation = useExportStructureStepLogsMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetStepLogStructuresResponse,
        void,
        StepLogStructureData,
        BaseExportRequest
    >(tableId, getStructureStepLogsQuery, undefined, undefined, getStructureStepLogsExportMutation);

    const isScenarioFilterActive = !!tableState.filter?.filter?.["eid"]?.[SearchFilterEnum.Equal];
    const scenarioRunId = tableFetchDataUtils.fetchQuery.isFetching
        ? undefined
        : tableFetchDataUtils.tableData?.structureLogs?.[0]?.stepRun?.scenario_run_id;

    const viewScenarioRunButton = isScenarioFilterActive ? (
        scenarioRunId ? (
            <TenantLink to={`/scenario/history/${scenarioRunId}`}>
                <Button icon={<NodeIndexOutlined />}>{tGA("view_scenario_run")}</Button>
            </TenantLink>
        ) : (
            <Button icon={<NodeIndexOutlined />} disabled>
                {tGA("view_scenario_run")}
            </Button>
        )
    ) : null;

    const { getRowHandlers, getRowClassName } = useRowHandlers<StepLogStructureData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.StructureStepLogDetail,
                drawerParam: `${DrawerId.StructureStepLogDetail}-id`,
                permission: PermissionEnum.StepLogStructuresView,
            },
        ],
    });

    const scenarioCategoryLabelKeys: Partial<Record<ConnectionCreatorScenarioCategory, string>> = {
        [ConnectionCreatorScenarioCategory.StructureValidate]: "enums.scenario_category.structure_validate",
        [ConnectionCreatorScenarioCategory.StructureModify]: "enums.scenario_category.structure_modify",
        [ConnectionCreatorScenarioCategory.StructureAutocomplete]: "enums.scenario_category.structure_autocomplete",
    };

    const columnConfig: TableColumnType<StepLogStructureData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.started")} />,
            dataIndex: "stepRun.started_at",
            key: "stepRun.started_at",
            render: (_, row) => <DateTimeCell value={row.stepRun?.started_at ?? undefined} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("created_at")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "origin_eid",
            key: "origin_eid",
            render: (_, row) => (row.origin_eid ? <ShortenedIdWithCopy id={row.origin_eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "eid",
            key: "eid",
            render: (_, row) => (row.eid ? <ShortenedIdWithCopy id={row.eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("run_id")} />,
            dataIndex: "step_run_id",
            key: "step_run_id",
            render: (_, row) => (row.step_run_id ? <ShortenedIdWithCopy id={row.step_run_id} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "step_run_id",
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.structure")} />,
            dataIndex: "formField.formStructure.name",
            key: "formField.formStructure.name",
            render: (_, row) => <EntityLinkCell.FormStructure entity={row.formField?.formStructure ?? null} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.formStructureSelect,
            filterName: "formField.formStructure.system_name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.form_field")} />,
            dataIndex: "formField.system_name",
            key: "formField.system_name",
            render: (_, row) => row.formField?.system_name ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "formField.system_name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tCC("enums.scenario_category.label")} />,
            dataIndex: "stepRun.scenarioRun.scenario.category",
            key: "stepRun.scenarioRun.scenario.category",
            render: (_, row) => {
                const category = row.stepRun?.scenarioRun?.scenario?.category as
                    | ConnectionCreatorScenarioCategory
                    | undefined;
                const labelKey = category ? scenarioCategoryLabelKeys[category] : undefined;
                return labelKey ? tCC(labelKey) : <EmptyCell />;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "stepRun.scenarioRun.scenario.category",
            filterOptions: Object.entries(scenarioCategoryLabelKeys).map(([value, labelKey]) => ({
                label: tCC(labelKey),
                value,
            })),
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "stepRun.scenarioRun.scenario.title",
            key: "stepRun.scenarioRun.scenario.title",
            render: (_, row) => <EntityLinkCell.Scenario entity={row.stepRun?.scenarioRun?.scenario} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "stepRun.scenarioRun.scenario.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.step")} />,
            dataIndex: "stepRun.title",
            key: "stepRun.title",
            render: (_, row) => row.stepRun?.title ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "stepRun.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.step_type")} />,
            dataIndex: "stepRun.type",
            key: "stepRun.type",
            render: (_, row) => {
                const type = row.stepRun?.type;
                if (!type) return <EmptyCell />;
                if (type === "structure") return getStepTypeLabel(type).split(" - ")[0];
                return getStepTypeLabel(type);
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.structureStepTypeSelect,
            filterName: "stepRun.type",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "http_status_code",
            key: "http_status_code",
            render: (_, row) => row.http_status_code ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "http_status_code",
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "state",
            key: "state",
            render: (_, row) => {
                const isOk = row.state === true;
                return (
                    <Tag icon={isOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isOk ? "green" : "red"}>
                        {isOk ? tGL("ok") : tGL("fail")}
                    </Tag>
                );
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "state",
            filterOptions: [
                { label: tGL("ok"), value: "true" },
                { label: tGL("fail"), value: "false" },
            ],
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
            dataIndex: "message",
            key: "message",
            render: (_, row) => (
                <StepLogMessageCell message={row.message} stepType={row.stepRun?.type} id={`message-${row.id}`} />
            ),
            width: 390,
            ellipsis: true,
        },
    ];

    return (
        <>
            <Table<GetStepLogStructuresResponse, StepLogStructureData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetStepLogStructuresResponse}
                dataSource={tableFetchDataUtils.tableData?.structureLogs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        filter: request.filter,
                        extension: exportState.extension,
                        headers: exportState.headers,
                        page: request.page,
                        export_all: exportState.range === "all",
                        delimiter: exportState.delimiter,
                    })
                }
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"stepRun.started_at"}
                autoScrollWidth={true}
                hideActionsColumn={true}
                extraTopBarElements={viewScenarioRunButton}
                extraFilters={[]}
            />

            {permissionsUtils.hasPermission(PermissionEnum.StepLogStructuresView) &&
                searchParams[`${DrawerId.StructureStepLogDetail}-id`] && (
                    <StructureStepLogDetailDrawer logId={searchParams[`${DrawerId.StructureStepLogDetail}-id`]!} />
                )}
        </>
    );
};
